import type { OrderItemRow, OrderRow } from '@/Redux/Slices/operationsApi';

/**
 * Order columns the API returns but `OrderRow` does not declare.
 *
 * `OrdersService.ORDER_INCLUDE` uses `include`, not `select`, so every scalar on
 * the model is already on the wire — the list type simply never named these
 * four. They are declared here rather than widened in the cache slice because
 * the board does not read them; only the detail panel does.
 */
export interface OrderDetailRow extends OrderRow {
  checkId?: string | null;
  placedAt?: string | null;
  completedAt?: string | null;
}

/** The money columns `computeCheckTotals` writes, as the API returns them. */
export interface CheckTotals {
  subtotal: string;
  discountTotal: string;
  serviceTotal: string;
  taxTotal: string;
  tipTotal: string;
  grandTotal: string;
  currency: string;
}

export interface TotalsBlock {
  subtotal: string;
  /** `null` when there is no check to read it from. */
  discountTotal: string | null;
  serviceTotal: string | null;
  taxTotal: string | null;
  tipTotal: string | null;
  total: string;
  currency: string | null;
  /** True when the four derived lines came from a Check, not from the Order. */
  fromCheck: boolean;
}

/**
 * The detail page's totals block.
 *
 * `Order` carries `subtotal` and `total` and nothing else — tax, discount,
 * service and tip are computed on the **Check** by `computeCheckTotals` from
 * `Charge` rows, and `Order.checkId` is nullable. So the four extra lines are a
 * check projection: with a check they are real, without one they do not exist
 * and the block honestly drops to two rows rather than printing zeroes.
 *
 * Service and tip stay in the block even though the design draws neither.
 * `grandTotal = subtotal − discount + service + tax`, so a check holding a FEE
 * or TIP charge rendered without them would show arithmetic that does not add
 * up — money silently missing from a bill is the one thing this screen must not
 * do. They are hidden only when they are genuinely zero.
 */
export function totalsBlock(
  order: Pick<OrderRow, 'subtotal' | 'total'>,
  check: CheckTotals | null | undefined,
): TotalsBlock {
  if (!check) {
    return {
      subtotal: order.subtotal,
      discountTotal: null,
      serviceTotal: null,
      taxTotal: null,
      tipTotal: null,
      total: order.total,
      currency: null,
      fromCheck: false,
    };
  }
  return {
    subtotal: check.subtotal,
    discountTotal: check.discountTotal,
    serviceTotal: check.serviceTotal,
    taxTotal: check.taxTotal,
    tipTotal: check.tipTotal,
    total: check.grandTotal,
    currency: check.currency,
    fromCheck: true,
  };
}

export type TimelineKey = 'placed' | 'preparing' | 'ready' | 'served' | 'completed';

export interface TimelineStep {
  key: TimelineKey;
  at: string;
}

type ItemStamps = Pick<OrderItemRow, 'preparingAt' | 'readyAt' | 'servedAt'>;

function earliest(values: (string | null)[]): string | null {
  const real = values.filter((value): value is string => Boolean(value));
  if (!real.length) return null;
  return real.reduce((min, value) => (value < min ? value : min));
}

/** `null` unless **every** line reached the step — a half-cooked order is not ready. */
function latestWhenAll(values: (string | null)[]): string | null {
  if (!values.length || values.some((value) => !value)) return null;
  return (values as string[]).reduce((max, value) => (value > max ? value : max));
}

/**
 * The order's timeline, from the timestamps that actually exist.
 *
 * There is no endpoint behind the design's five fixed steps. `Order` stores
 * `placedAt` and `completedAt`; the intermediate transitions live only as
 * `OrderStatus` values and are never stamped on the order. What *is* stamped is
 * per line — `preparingAt`, `readyAt`, `servedAt` — so the middle of the rail is
 * an aggregate of the lines, and the screen labels it as one.
 *
 * "Sent to kitchen" is therefore absent: it has no timestamp anywhere. The only
 * per-transition log in the product is `GET /audit/entity/Order/:id`, which is
 * gated on `reports.view` — a different permission from the one this page needs.
 */
export function orderTimeline(
  order: Pick<OrderDetailRow, 'createdAt' | 'placedAt' | 'completedAt'> & { items: ItemStamps[] },
): TimelineStep[] {
  const steps: TimelineStep[] = [];
  const placed = order.placedAt ?? order.createdAt;
  if (placed) steps.push({ key: 'placed', at: placed });

  const items = order.items ?? [];
  if (items.length) {
    const preparing = earliest(items.map((item) => item.preparingAt));
    if (preparing) steps.push({ key: 'preparing', at: preparing });

    const ready = latestWhenAll(items.map((item) => item.readyAt));
    if (ready) steps.push({ key: 'ready', at: ready });

    const served = latestWhenAll(items.map((item) => item.servedAt));
    if (served) steps.push({ key: 'served', at: served });
  }

  if (order.completedAt) steps.push({ key: 'completed', at: order.completedAt });
  return steps;
}
